/**
 * @ngdoc run
 * @memberof mercury.startup
 * @name StateChangeListeners
 * @description Setup global state change listeners for checking of access and resetting service defaults
 */
angular.module('mercury.startup')
.run(["$window", "$filter", "multisite", "$rootScope", "reporttime", "$state", "title", "userinfo", "applicationMenu", "$urlRouter", "logger", function($window, $filter, multisite, $rootScope, reporttime, $state, title, userinfo, applicationMenu, $urlRouter, logger) {
  // These variables hold state representing where a navigation has started from and is going to.
  // We need this to coordinate the AngularJS and React routers correctly, on this side of things
  // only telling the React app a navigation has occurred from within AngularJS if that is where
  // the navigation originated from (i.e. it was initiated from a link clicked that was rendered by
  // AngularJS).
  //
  // - angularjs = a link to an AngularJS page
  // - react = a link to a React page, rendered by AngularJS
  // - react-internal = a navigation that occurred from one React page to another React page (i.e AngularJS wasn't involved)
  var fromPageType = 'angularjs';
  var toPageType = 'angularjs';

  // Listen for navigation notifications emitted by the React app telling us that there
  // has been a react page to react page navigation occur.
  // Due to the two routers that run in tandem, we won't actually hear things here in
  // AngularJS with any of the standard state change listeners, however we do need
  // to know about it in order to understand if we should tell the React app about a
  // subsequent navigation that has started from the AngularJS app.
  $window.addEventListener('@@loaded/reactNavigation', function() {
    // When an internal React app navigation occurs we need to make sure that AngularJS
    // router knows that this has occurred. If the router here doesn't know then when a
    // subsequent navigation initiated from AngularJS happens it gets confused about where
    // it already is and can end up requiring multiple clicks on the AngularJS rendered link
    // before the navigation actually runs.
    $state.transitionTo('Blank', null, {location: false, notify: false, reload: false});
    fromPageType = 'react-internal';
    toPageType = $state.current.controller === 'ReactCtrl' ? 'react-internal' : 'angularjs';
  });

  // This event is emitted by AngularJS before the $locationChangeSuccess event we also listen for.
  // We use this to keep track of where the navigation that is currently happening originated from,
  // using the name of the controller as a means of identifying the React app routes. Note that when
  // a navigation is occurring solely within the React app this event doesn't fire - so this one lets us
  // keep track of navigations to React routes from AngularJS.
  $rootScope.$on('$stateChangeStart', function(_ev, toState, _toParams, fromState, _fromParams) {
    logger.debug('$stateChangeStart handler called with toState %o and fromState %o, toStateParams %o and fromStateParams %o', toState, fromState, _toParams, _fromParams);
    if (fromState.controller === 'ReactCtrl') {
      fromPageType = 'react';
    } else {
      fromPageType = 'angularjs';
    }

    if (toState.controller === 'ReactCtrl') {
      toPageType = 'react';
    } else {
      toPageType = 'angularjs';
    }
  });

  // Make sure the React app is aware of AngularJS navigation events.
  $rootScope.$on('$locationChangeSuccess', function(e, newUrl, oldUrl, newState, oldState) {
    e.preventDefault();
    var url = location.hash.substring(1, location.hash.length);
    // Tell the React app that we're navigating to a new page...
    logger.debug('In $locationChangeSuccess with newUrl %o, oldUrl %o, newState %o, oldState %o', newUrl, oldUrl, newState, oldState);
    // We only want to tell the React app about a navigation that originated from
    // within the AngularJS app. I.e. we don't want to echo back to the React app
    // that a navigation has occurred when the React app was the one that initiated
    // that navigation (because this leads to multiple renders of the React app, and
    // by extension this results in the loader requests being sent multiple times too).
    if (fromPageType !== toPageType || fromPageType !== 'react-internal') {
      logger.debug('AngularJS $locationChangeSuccess, synchronising with React router navigation to %s (new state in AngularJS is %o)', url, newState);
      window.dispatchEvent(new CustomEvent('@@loaded/angularNavigation', {detail: {to: url}}));
    } else {
      logger.debug('AngularJS $locationChangeSuccess, triggered on react route - not sending an echo back');
    }

    // ...and then let the AngularJS router handle it's navigation.
    $urlRouter.sync();
  });

  // There are scenarios where we trigger a reload of the page in AngularJS by
  // navigating to the current state again (e.g. when changing the company). To
  // ensure the React app is aware of this and re-renders we listen for them
  // and announce the navigation to the React app to trigger a re-render there.
  $rootScope.$on('$stateChangeStart', function(event, toState, toStateParams, fromState, fromStateParams) {
    if (
      toState.url === fromState.url &&
      JSON.stringify(toStateParams) === JSON.stringify(fromStateParams)
    ) {
      // we need to use the location url here as the AngularJS state isn't
      // reliable, any React routes with path placeholders will exist in the
      // AngularJS state with the placeholder value still present - we can't
      // navigate to that.
      var url = location.hash.substring(1, location.hash.length);
      logger.debug('State change to identical state triggered, notifying React so it can re-mount');
      window.dispatchEvent(new CustomEvent('@@loaded/angularNavigation', {detail: {to: url, updatedPageContent: true}}));
    }
  });

  // Now we've registered our own location change listener we enable the
  // standard one.
  $urlRouter.listen();

  // On state change start, reset some services back to their default values.
  $rootScope.$on('$stateChangeStart', function(event, toState, toStateParams, fromState, fromStateParams) {
    // We don't reset the state if the state name didn't change (Just changed report period), or if the to or from state isn't a complete page.
    var statenameHasChanged = toState.name != fromState.name;
    var notActionState = !toState.actionState;
    var notToDialog = !(toState.data && toState.data.dialogState);
    var notFromDialog = !(fromState.data && fromState.data.dialogState);
    if (statenameHasChanged && notActionState && notToDialog && notFromDialog) {
      reporttime.setAllowedTypes(['day', 'week', 'month', 'custom']);
      reporttime.defaultType = 'day';
      reporttime.restrictToCurrent = false;
      reporttime.setFormatString(false);
      title.set('');

      // Clear multisite status
      multisite.setMultisite(false);
    }

    if (
      toStateParams.hasOwnProperty('report') &&
      toStateParams.report == 'default'
    ) {
      // If a menu has been defined to have a report state value of 'default'
      // it means that we never want to maintain a reporting period from the
      // url when navigating to this state. We hack around the section of code
      // below that forces the report state value onto any route that supports
      // it by removing it from the state entirely and triggering a reload of
      // the page. This doesn't seem like it should work, but does - I'd rather
      // move away from AngularJS that try and get a perfect fix here!
      logger.info('Default report param detected, removing report value completely from the state');
      delete toStateParams.report;
      $state.go(toState.name, toStateParams, { inherit: true });
    }

    // If the current route has a report state param, and we're navigating to a
    // route that supports a report state param but hasn't requested one, then
    // this code 'helpfully' copies the report state param across to the new
    // route.
    //
    // This is needed because the menu structure of the app doesn't know about
    // the value of the report state param. This is because the ui-sref attribute
    // is set when we first load the page, and the report param can change by
    // user action on the page. I.e. the link we've bound to the page can't go
    // and change the report param on the fly.
    if (
      toStateParams.hasOwnProperty('report') &&
      !toStateParams.report &&
      fromStateParams.report
    ) {
        event.preventDefault();
        toStateParams.report = fromStateParams.report;
        $state.go(toState.name, toStateParams);
    }
  });

  // On state change success, ensure the user has access to this report
  $rootScope.$on('$stateChangeSuccess', function() {
    var stateNameParts = $state.current.name.split('.', 1);
    var layout = stateNameParts[0];

    applicationMenu.getMenu().then(function(companyReports) {
      var menuSections = $filter('filter')(companyReports, {label: layout});
      if (menuSections.length !== 1 && ['Admin', 'Settings', 'FullWidth', 'FullScreen'].indexOf(layout) === -1) {
        console.log('ERR42: redirecting to default report');
        console.log('stateNameParts:' + stateNameParts);
        console.log('layout: ' + layout);
        console.log('MenuSections:');
        console.log(menuSections);

        userinfo.companyDefaultReport().then(function(defaultReport) {
          $state.go(defaultReport);
        });
        return;
      }

      if ($state.current.name == layout) {
        var menu = menuSections[0].menuItems;
        if (menu[0].subMenu.length > 0) {
          $state.go(menu[0].subMenu[0].state);
        } else {
          $state.go(menu[0].state);
        }
      }
    });
  });
}]);
